<script setup lang="ts">
import AppLayout from '../layouts/AppLayout.vue'
import { ref, onMounted, computed, watch } from 'vue'
import { useAuth } from '../composables/useAuth'
import { useRoute, useRouter } from 'vue-router'
import { getSupplierProducts, getTaxonomy, deleteSupplierProduct } from '../services/api'
import { paginate } from '../components/supplier/company-profile/productList'
import { PAGE_SIZES, catalogQueryFromRoute, catalogQueryToRoute, filterSupplierProducts, type CatalogQuery } from '../utils/supplierProductFilters'
import { useCurrentContext } from '../composables/useCurrentContext'
import type { TaxonomyArea, SupplierProductListItem } from '../types/api'
import ConfirmDeleteModal from '../components/ConfirmDeleteModal.vue'
import { useUserStore } from '../stores/user'
import { trackEvent } from '../services/analytics'

const { supplierId } = useCurrentContext()
const userStore = useUserStore()
const canEditProducts = computed(() => userStore.isImpersonating || userStore.supplierTier === 'ENTERPRISE')
const router = useRouter();
const route = useRoute()

const { getAccessTokenSilently } = useAuth()
const loading = ref(true)
const products = ref<SupplierProductListItem[]>([])
const taxonomy = ref<TaxonomyArea[]>([])

// Search, filters, page and page size live in the URL so a refresh keeps them.
const catalog = computed(() => catalogQueryFromRoute(route.query))
const setCatalog = (patch: Partial<CatalogQuery>) =>
  router.replace({ query: catalogQueryToRoute({ ...catalog.value, ...patch }) })
// Any filter change resets to page 1; changing the area also clears the subcategory.
const search = computed({ get: () => catalog.value.q, set: q => setCatalog({ q, page: 1 }) })
const selectedArea = computed({ get: () => catalog.value.area, set: area => setCatalog({ area, sub: '', page: 1 }) })
const selectedSubcategory = computed({ get: () => catalog.value.sub, set: sub => setCatalog({ sub, page: 1 }) })
const pageSize = computed({ get: () => catalog.value.size, set: size => setCatalog({ size, page: 1 }) })
const page = computed({ get: () => catalog.value.page, set: page => setCatalog({ page }) })

// Delete State
const showDeleteModal = ref(false)
const productToDelete = ref<SupplierProductListItem | null>(null)
const deleting = ref(false)

const loadData = async () => {
    try {
    if (!supplierId.value) return // Wait for context
    const token = await getAccessTokenSilently()
    const [prods, tax] = await Promise.all([
      getSupplierProducts(supplierId.value, token),
      getTaxonomy()
    ])
    products.value = prods
    taxonomy.value = tax
    trackEvent('supplier_product_list_viewed', {
      supplier_id: supplierId.value,
      product_count: prods.length,
    })
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

function openProductEditor(productId: string) {
    trackEvent('supplier_product_edit_opened', { supplier_id: supplierId.value, product_id: productId })
    router.push(`/supplier/products/${productId}/edit`)
}

onMounted(() => {
    loadData()
})

// Reload if context changes (e.g. reload page)
watch(supplierId, (newId) => {
    if(newId) loadData()
})

const availableSubcategories = computed(() => {
  if (!selectedArea.value) return []
  const area = taxonomy.value.find(a => a.id === selectedArea.value)
  return area ? area.subcategories : []
})

const filteredProducts = computed(() => filterSupplierProducts(products.value, catalog.value))
const view = computed(() => paginate(filteredProducts.value, page.value, pageSize.value))

// Helper to get primary area (first one found)
function getPrimaryArea(product: SupplierProductListItem): string {
  const firstSubcat = product.subcategories?.[0]
  if (!firstSubcat) return '-'
  return firstSubcat.subcategory.area.name
}

function promptDelete(product: SupplierProductListItem) {
    productToDelete.value = product
    showDeleteModal.value = true
}

async function handleDelete() {
    if (!productToDelete.value || !supplierId.value) return
    
    deleting.value = true
    try {
        const token = await getAccessTokenSilently()
        await deleteSupplierProduct(supplierId.value, productToDelete.value.id, token)
        
        // Remove from list locally to avoid reload flicker
        products.value = products.value.filter(p => p.id !== productToDelete.value?.id)
        showDeleteModal.value = false
        productToDelete.value = null
    } catch (e: any) {
        console.error(e)
        alert(e?.response?.data?.error || 'Failed to delete product.')
    } finally {
        deleting.value = false
    }
}
</script>

<template>
  <AppLayout>
    <div class="space-y-6">
      <div class="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div>
           <h1 class="text-2xl font-bold text-cc-text">Product Catalog</h1>
           <p class="text-cc-text-secondary">Manage your product listings and details.</p>
        </div>
        <div v-if="canEditProducts" class="flex gap-2">
           <router-link to="/supplier/products/new" class="bg-cc-primary hover:bg-cc-primary-hover text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
             + New Product
           </router-link>
        </div>
      </div>

      <!-- Filters -->
      <div class="bg-cc-elevated p-4 rounded-lg shadow-sm border border-cc-border flex flex-col sm:flex-row gap-4">
         <div class="flex-1">
             <input v-model="search" type="text" placeholder="Search products..." class="w-full border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none" />
         </div>
         <div class="w-full sm:w-48">
             <select v-model="selectedArea" class="w-full border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none">
                 <option value="">All Solution Areas</option>
                 <option v-for="area in taxonomy" :key="area.id" :value="area.id">{{ area.name }}</option>
             </select>
         </div>
         <div class="w-full sm:w-48">
             <select v-model="selectedSubcategory" :disabled="!selectedArea" class="w-full border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600">
                 <option value="">All Subcategories</option>
                 <option v-for="sub in availableSubcategories" :key="sub.id" :value="sub.id">{{ sub.name }}</option>
             </select>
         </div>
      </div>

      <div v-if="loading" class="text-center py-12 text-gray-500">Loading products...</div>
      
      <!-- No overflow-hidden on the card: it would trap the sticky header.
           Sticky offsets are measured from AppLayout <main>'s content edge, so the
           negative top cancels its p-4 / md:p-8 padding and pins the header at the top. -->
      <div v-else class="bg-cc-elevated rounded-lg shadow-sm border border-cc-border">
        <table class="w-full text-sm text-left">
           <thead class="sticky -top-4 md:-top-8 z-10 bg-gray-50 dark:bg-gray-700 text-cc-text-secondary border-b border-cc-border uppercase text-xs [&_th:first-child]:rounded-tl-lg [&_th:last-child]:rounded-tr-lg">
              <tr>
                 <th class="px-6 py-3">Product Name</th>
                 <th class="px-6 py-3">Solution Area</th>
                 <th class="px-6 py-3">Subcategories</th>
                 <th class="px-6 py-3">Pricing Band</th>
                 <th class="px-6 py-3 text-right">Actions</th>
              </tr>
           </thead>
           <tbody class="divide-y divide-gray-50 dark:divide-gray-700">
              <tr v-for="product in view.rows" :key="product.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer" @click="router.push(canEditProducts ? `/supplier/products/${product.id}/edit` : `/supplier/products/${product.id}`)">
                 <td class="px-6 py-4">
                    <div class="font-medium text-cc-text">{{ product.name }}</div>
                    <div class="text-xs text-cc-text-secondary truncate max-w-md">{{ product.description }}</div>
                 </td>
                 <td class="px-6 py-4">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                        {{ getPrimaryArea(product) }}
                    </span>
                 </td>
                 <td class="px-6 py-4">
                    <div class="flex flex-wrap gap-1">
                        <!-- Legacy Categories (Fallback) -->
                        <template v-if="(!product.subcategories || product.subcategories.length === 0) && product.categories">
                             <span v-for="cat in product.categories" :key="cat" class="px-2 py-0.5 bg-cc-hover rounded text-xs text-cc-text-secondary border border-cc-border">{{ cat }}</span>
                        </template>
                        <!-- New Taxonomy -->
                        <template v-else>
                            <span v-for="ps in product.subcategories" :key="ps.subcategory.id" class="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded text-xs border border-emerald-100 dark:border-emerald-900/30">
                                {{ ps.subcategory.name }}
                            </span>
                        </template>
                    </div>
                 </td>
                 <td class="px-6 py-4 text-cc-text-secondary">
                     <span v-if="product.pricing_bands && product.pricing_bands.length > 0">
                         {{ product.pricing_bands.join(', ') }}
                     </span>
                     <span v-else>{{ product.pricing_band || '-' }}</span>
                 </td>
                 <td class="px-6 py-4 text-right" @click.stop>
                    <div v-if="canEditProducts" class="flex justify-end gap-3">
                        <button @click="openProductEditor(product.id)" class="text-cc-success hover:text-emerald-800 dark:hover:text-emerald-300 font-medium text-xs">Edit</button>
                        <button @click="promptDelete(product)" class="text-cc-danger hover:text-red-800 dark:hover:text-red-300 font-medium text-xs">Delete</button>
                    </div>
                    <div v-else>
                        <button @click="router.push(`/supplier/products/${product.id}`)" class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium text-xs">View</button>
                    </div>
                 </td>
              </tr>
              <tr v-if="filteredProducts.length === 0">
                 <td colspan="5" class="px-6 py-8 text-center text-cc-text-tertiary">No products found matching filters.</td>
              </tr>
           </tbody>
        </table>
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-3 border-t border-cc-border text-sm text-cc-text-secondary">
          <span data-test="range">Showing {{ view.from }}–{{ view.to }} of {{ view.total }}</span>
          <div class="flex items-center gap-3">
            <label class="flex items-center gap-1.5">
              Rows per page
              <select v-model.number="pageSize" class="border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-cc-primary focus:outline-none">
                <option v-for="n in PAGE_SIZES" :key="n" :value="n">{{ n }}</option>
              </select>
            </label>
            <span>Page {{ view.page }} of {{ view.pages }}</span>
            <button type="button" :disabled="view.page <= 1" @click="page = view.page - 1" class="px-3 py-1 rounded-md border border-cc-border-med text-cc-text hover:bg-cc-hover disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <button type="button" :disabled="view.page >= view.pages" @click="page = view.page + 1" class="px-3 py-1 rounded-md border border-cc-border-med text-cc-text hover:bg-cc-hover disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
            :is-open="showDeleteModal"
            title="Delete Product"
            :message="`Are you sure you want to delete '${productToDelete?.name}'? This action cannot be undone.`"
            :loading="deleting"
            @confirm="handleDelete"
            @close="showDeleteModal = false"
      />
    </div>
  </AppLayout>
</template>
